/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import { useRecoilState } from 'recoil';
import { Modal, Button, connectModal } from '@gsa/afp-component-library';
import MakeAndModelSearchPanel from './make-and-model-select-panel';
import CommentSection from '../../make-model/make-model-modal-comment-section';
import { useMakeModelApprovalPanelProvider } from './make-model-approval-provider';
import { makeModelStatusMessageAtom } from '../../atoms/make-model-atoms';
import { EDIT_MAKE_MODEL_APPROVAL_REQUEST } from '../../queries.gql';
import MakeModelSummary from '../../components/make-model-summary';

const EditMakeModelApprovalModal = ({ onClose, data, refetchData }) => {
  const [commentError, setCommentError] = useState('');
  const hasChassis = Boolean(data?.chassisMakeCode && data?.chassisModelCode);
  const {
    comment,
    setComment,
    bodyMake,
    bodyMakeInputValue,
    bodyModel,
    bodyModelInputValue,
    chassisMake,
    chassisMakeInputValue,
    chassisModel,
    chassisModelInputValue,
    setDirtyFields,
  } = useMakeModelApprovalPanelProvider();

  const requiredMsg = 'This is a required field';

  // The shared TypeAhead group tracks a `{ label, value }` option when one is
  // picked from the list, and falls back to the raw input value when the user
  // types a custom (unmatched) make/model. Collapse both into the code + name
  // the mutation expects.
  const makeCode = bodyMake?.value ?? '';
  const makeName = bodyMake?.label || bodyMakeInputValue;
  const modelCode = bodyModel?.value ?? '';
  const modelName = bodyModel?.label || bodyModelInputValue;
  const chassisMakeCode = chassisMake?.value ?? '';
  const chassisMakeName = chassisMake?.label || chassisMakeInputValue;
  const chassisModelCode = chassisModel?.value ?? '';
  const chassisModelName = chassisModel?.label || chassisModelInputValue;

  const [, setToaster] = useRecoilState(makeModelStatusMessageAtom);

  const originalRequestData = {
    makeCode: data?.bodyMake?.makeCode,
    makeName: data?.bodyMake?.makeName,
    modelCode: data?.bodyModel?.modelCode,
    modelName: data?.bodyModel?.modelName,
    chassisMakeCode: data?.chassisMake?.makeCode,
    chassisMakeName: data?.chassisMake?.makeName,
    chassisModelCode: data?.chassisModel?.modelCode,
    chassisModelName: data?.chassisModel?.modelName,
  };

  const [editMakeModelApprovalRequest] = useMutation(
    EDIT_MAKE_MODEL_APPROVAL_REQUEST,
    {
      onCompleted: () => {
        setToaster({
          type: 'success',
          message: (
            <>
              You have successfully approved {makeName} and {modelName}{' '}
              {chassisMakeName && chassisModelName
                ? `/ ${chassisMakeName} - ${chassisModelName}`
                : ''}
              .
            </>
          ),
        });
        refetchData();
        onClose();
      },
      onError: () => {
        setToaster({
          type: 'error',
          message: (
            <>
              There was an error while approving {makeName} and {modelName}{' '}
              {chassisMakeName && chassisModelName
                ? `/ ${chassisMakeName} - ${chassisModelName}`
                : ''}
              .
            </>
          ),
        });
        refetchData();
        onClose();
      },
    },
  );

  const modalTitle = 'Edit make and model';
  const modalSubTitle = 'Are you sure you want to edit this make and model?';

  const onCommentChange = (value) => {
    setComment(value);
    setCommentError(value?.trim() ? '' : requiredMsg);
  };

  const onUpdateMakeModel = () => {
    // enable error message for all make-model input fields
    setDirtyFields(['bodyMake', 'bodyModel', 'chassisMake', 'chassisModel']);

    // Validate comment
    if (!comment?.trim()) {
      setCommentError(requiredMsg);
      return;
    }

    if (
      makeName &&
      modelName &&
      (!hasChassis || (chassisMakeName && chassisModelName))
    ) {
      // prepare the input for the mutation
      const editMakeModelApprovalRequestInput = {
        makeModelApprovalId: Number.parseInt(data?.makeModelApprovalId, 10),
        makeCode: Number.parseInt(makeCode, 10),
        makeName,
        modelCode,
        modelName,
        ...(chassisMakeName && {
          chassisMakeCode: Number.parseInt(chassisMakeCode, 10),
          chassisMakeName,
        }),
        ...(chassisModelName && {
          chassisModelCode,
          chassisModelName,
        }),
        comment: comment.trim(),
        originalRequestData,
      };
      editMakeModelApprovalRequest({
        variables: { editMakeModelApprovalRequestInput },
      });
    }
  };

  return (
    <div className="afp-modal-wrapper">
      <div className="afp-modal-overlay">
        <Modal
          id="edit-make-model-approval-modal"
          className="edit-make-model-approval-request-modal"
          variant="extra-large"
          title={
            <h1 tabIndex="0" aria-label={modalTitle}>
              {modalTitle}
            </h1>
          }
          onClose={onClose}
          actions={
            <div>
              <Button
                data-testid="edit-make-model-approval-modal-cancel-btn"
                variant="unstyled"
                onClick={onClose}
                label="Cancel"
              />
              <Button
                data-testid="edit-make-model-approval-modal-save-btn"
                variant="primary"
                onClick={onUpdateMakeModel}
                label="Save and approve"
              />
            </div>
          }
        >
          <div className="padding-top-2 padding-bottom-6">
            <span tabIndex="0" aria-label={modalSubTitle}>
              {modalSubTitle}
            </span>
            <div className="margin-top-2">
              <MakeModelSummary
                make={data?.bodyMake}
                modelName={data?.bodyModel?.modelName}
                modelCode={data?.bodyModel?.modelCode}
                nhtsaModelCode={data?.bodyModel?.nhtsaModelCode}
                chassisMake={data?.chassisMake}
                chassisModel={data?.chassisModel}
              />
            </div>
          </div>
          <div>
            <MakeAndModelSearchPanel hasChassis={hasChassis} />
            <CommentSection
              commentBody={comment}
              onCommentChange={onCommentChange}
              required
              errorMessage={commentError}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};

EditMakeModelApprovalModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    makeCode: PropTypes.number.isRequired,
    makeName: PropTypes.string.isRequired,
    modelCode: PropTypes.string.isRequired,
    modelName: PropTypes.string.isRequired,
    chassisMakeCode: PropTypes.number,
    chassisModelCode: PropTypes.string,
    makeModelApprovalId: PropTypes.number.isRequired,
  }).isRequired,
  refetchData: PropTypes.func.isRequired,
};

export default connectModal(EditMakeModelApprovalModal);
